




















































// eslint-disable-next-line import/order
import FullCalendar, { CalendarOptions } from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { defineComponent, ref, computed, onUnmounted, watch, PropType, ComputedRef } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import ShopEventCard from '~/components/pages/Shop/ShopEventCard.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import { useTags } from '~/composables/contents/useTags';
import { useStore } from '~/composables/useStore';
import { useWOVNio } from '~/composables/wovn/useWOVNio';
import { SHOP_ANCHOR } from '~/constants';
import { EventCardTags } from '~/types/modules/event';
import { DisplayLang } from '~/enums';

export default defineComponent({
  components: {
    ShopEventCard,
    AppSeeMoreButton,
    FullCalendar,
    AppIcon,
    AppLoader,
  },
  props: {
    shopName: {
      type: String,
      required: true,
    },
    isInitialized: {
      type: Boolean,
      required: true,
    },
    tags: {
      type: Array as PropType<EventCardTags[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const { currentLang } = useWOVNio();
    // 半年後・半年前の月をそれぞれ取得
    const prevHalfYearMonth = dayjs().subtract(6, 'month').format('YYYY-MM');
    const nextHalfYearMonth = dayjs().add(6, 'month').format('YYYY-MM');
    const selectedCount = ref(1);
    const prevButtonName = '.prev-month';
    const nextButtonName = '.next-month';
    // イベントAPI用のページ番号
    const eventPageNo = ref(1);
    // イベント検索用の日付
    const eventSearchDate = ref(dayjs().format('YYYY-MM-DD'));
    const { findTagsByEventId } = useTags();
    const selectedDate = ref(dayjs().format('YYYY-MM-DD'));

    const shopEventList = computed(() => {
      return store.state.event.list.shopEventList;
    });

    /** イベントリストのもっと見るボタン制御 */
    const isMoreShopEvents = computed(() => {
      // 0件の場合はそのまま非表示
      if (store.state.event.list.totalEventCounts === 0) {
        return false;
      }
      // storeに保存されているイベント数よりもトータル数の方が多いか否か
      return store.state.event.list.shopEventList.length < store.state.event.list.totalEventCounts;
    });

    const seeMoreEvents = async () => {
      eventPageNo.value++;
      emit('fetchEvents', eventSearchDate.value, eventPageNo.value);
    };

    /** 選択びに表示を変えるクラスを付与  */
    const formatSelectedDateStyle = (date: string, isAddStyle: boolean) => {
      const nodeList: NodeListOf<HTMLElement>[] = [];
      nodeList.push(document.querySelectorAll<HTMLElement>(`td[data-date = "${date}"]`));
      if (isAddStyle) {
        nodeList.forEach((v) => v.forEach((x) => x.classList.add('selected-day')));
      } else {
        nodeList.forEach((v) => v.forEach((x) => x.classList.remove('selected-day')));
      }
    };

    /** 共通のカレンダーオプション値 */
    const commonOptions: ComputedRef<CalendarOptions> = computed(() => {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: currentLang.value.code === DisplayLang.JA ? 'ja' : 'en',
        // "日"をはずして表示する
        dayCellContent(e: any) {
          e.dayNumberText = e.dayNumberText.replace('日', '');
        },
        firstDay: 1,
        fixedWeekCount: false,
        // イベント開催日を設定
        events: store.state.event.list.datesOfEvents.map((date) => {
          return { start: date.toString(), title: dayjs(date.toString()).format('D') };
        }),
        // クリックした日付以降のイベント一覧を再取得
        dateClick: async (e: any) => {
          // 選択日が同一であればイベント情報を取得、表示を変える
          if (dayjs(e.date).format('YYYY-MM-DD') !== selectedDate.value) {
            formatSelectedDateStyle(selectedDate.value, false);
            fetchEvents(e.date);
            selectedDate.value = dayjs(e.date).format('YYYY-MM-DD');
            formatSelectedDateStyle(selectedDate.value, true);
          }
        },
        eventClick: async (e: any) => {
          fetchEvents(e.event.start);
        },
      };
    });

    /** 特定のクラス名のボタンを活性・日活性させる */
    const toggleButton = (className: string, disabled: boolean) => {
      const nodeButtonList: NodeListOf<HTMLInputElement>[] = [];
      nodeButtonList.push(document.querySelectorAll<HTMLInputElement>(className));
      nodeButtonList.forEach((v) =>
        v.forEach((y) => {
          y.disabled = disabled;
        })
      );
    };

    /** 特定のクラス名のボタンをクリックさせる */
    const clickFunction = (className: string) => {
      const nodeButtonList: NodeListOf<HTMLElement>[] = [];
      nodeButtonList.push(document.querySelectorAll<HTMLElement>(className));
      nodeButtonList.forEach((v) =>
        v.forEach((y) => {
          y.click();
        })
      );
    };

    /** イベント日にクラスを付与する（表示を変えるため） */
    const formatStyle = () => {
      const nodeList: NodeListOf<HTMLElement>[] = [];
      store.state.event.list.datesOfEvents.forEach((date) =>
        nodeList.push(document.querySelectorAll<HTMLElement>(`td[data-date = "${date}"]`))
      );
      nodeList.forEach((v) => v.forEach((x) => x.classList.add('event-day')));
    };

    /** 指定の日時を起点にイベント情報を取得する */
    const fetchEvents = (selectedDate: string) => {
      // イベント一覧情報の初期化が済んでいない場合は早期リターン（多重クリックなど）
      if (!props.isInitialized) {
        return;
      }
      // リクエスト用のページ番号を初期化
      eventPageNo.value = 1;
      eventSearchDate.value = dayjs(selectedDate).format('YYYY-MM-DD');
      emit('fetchEvents', eventSearchDate.value, 1);
    };

    /** 全ての前月へ戻るボタンを押下 */
    const moveToPrevMonth = () => {
      clickFunction('.fc-prev-button');
      formatSelectedDateStyle(selectedDate.value, true);
    };

    /** 全ての次月へ進むボタンを押下 */
    const moveToNextMonth = () => {
      clickFunction('.fc-next-button');
      formatSelectedDateStyle(selectedDate.value, true);
    };

    /** PC用の左カレンダーオプション値 */
    const pcLeftOptions = computed(() => {
      return {
        ...commonOptions.value,
        headerToolbar: {
          start: 'prev',
          center: 'title',
          end: 'next',
        },
        // prevのボタン表示切替
        datesSet(info: any) {
          formatStyle();
          const viewYearMonth = dayjs(info.view.currentStart).format('YYYY-MM');
          viewYearMonth === prevHalfYearMonth
            ? toggleButton(prevButtonName, true)
            : toggleButton(prevButtonName, false);
        },
      };
    });

    /** PC用の右カレンダーオプション値 */
    const pcRightOptions = computed(() => {
      return {
        ...commonOptions.value,
        headerToolbar: {
          start: 'prev',
          center: 'title',
          end: 'next',
        },
        // 表示する月を指定（当月・次月）
        initialDate: dayjs().add(selectedCount.value, 'month').format(),
        // nextのボタン切り替え
        datesSet(info: any) {
          formatStyle();
          const viewYearMonth = dayjs(info.view.currentStart).format('YYYY-MM');
          viewYearMonth === nextHalfYearMonth
            ? toggleButton(nextButtonName, true)
            : toggleButton(nextButtonName, false);
        },
      };
    });

    /** SP用のカレンダーオプション値 */
    const spOptions = computed(() => {
      return {
        ...commonOptions.value,
        headerToolbar: {
          start: 'prev',
          center: 'title',
          end: 'next',
        },
        // prev, nextのボタン表示切替
        datesSet(info: any) {
          formatStyle();
          const viewYearMonth = dayjs(info.view.currentStart).format('YYYY-MM');
          viewYearMonth === prevHalfYearMonth
            ? toggleButton(prevButtonName, true)
            : toggleButton(prevButtonName, false);
          viewYearMonth === nextHalfYearMonth
            ? toggleButton(nextButtonName, true)
            : toggleButton(nextButtonName, false);
        },
      };
    });

    // storeのイベント日付の変更に応じてスタイルを修正
    watch(
      () => store.state.event.list.datesOfEvents,
      () => {
        formatStyle();
      }
    );

    /** ページ離脱前にイベント日をリセット */
    onUnmounted(() => {
      store.commit('event/list/resetDatesOfEvents');
    });

    return {
      SHOP_ANCHOR,
      pcRightOptions,
      pcLeftOptions,
      spOptions,
      moveToPrevMonth,
      moveToNextMonth,
      seeMoreEvents,
      shopEventList,
      isMoreShopEvents,
      findTagsByEventId,
    };
  },
});
